/**
 * One video playing at a time, and a video that scrolls out of view pauses (Speedies build it;
 * chat adopts it in ticket 14zfy41wxvm). Imports nothing Speedy-specific.
 *
 *   * When a registered element starts playing, every other registered element pauses.
 *   * When an element drops below `threshold` of itself visible inside its `root`, it pauses. It is
 *     never resumed on its own: scrolling back shows it paused.
 *   * `pauseAll()` is for overlays that take over the screen.
 */
interface Entry {
  el: HTMLMediaElement;
  onPlay: () => void;
  observer: IntersectionObserver | null;
}

const entries = new Map<string, Entry>();

export function register(
  id: string,
  el: HTMLMediaElement,
  opts: { root?: Element | null; threshold?: number } = {},
): () => void {
  unregisterId(id);
  const onPlay = () => {
    for (const [otherId, e] of entries) if (otherId !== id && !e.el.paused) e.el.pause();
  };
  el.addEventListener('play', onPlay);
  const threshold = opts.threshold ?? 0.5;
  let observer: IntersectionObserver | null = null;
  if (typeof IntersectionObserver !== 'undefined') {
    observer = new IntersectionObserver(
      ([entry]) => {
        if (entry && entry.intersectionRatio < threshold && !el.paused) el.pause();
      },
      { root: opts.root ?? null, threshold: [0, threshold] },
    );
    observer.observe(el);
  }
  entries.set(id, { el, onPlay, observer });
  return () => {
    const current = entries.get(id);
    if (current && current.el === el) unregisterId(id);
  };
}

function unregisterId(id: string): void {
  const e = entries.get(id);
  if (!e) return;
  e.el.removeEventListener('play', e.onPlay);
  e.observer?.disconnect();
  entries.delete(id);
}

export function pauseAll(): void {
  for (const e of entries.values()) if (!e.el.paused) e.el.pause();
}

/** Test seam. */
export function registeredCount(): number {
  return entries.size;
}
